import type { GetServerSideProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { NextSeo } from 'next-seo'
import React from 'react'
import { OG_IMAGE_HEIGHT, OG_IMAGE_WIDTH } from 'src/constants/ogImages'
import { PillarPage } from 'src/constants/searchPages'
import AlmacenamientoADomicilio from 'src/screens/AlmacenamientoADomicilio'
import { numberWithCommas } from 'src/utils'
import { loadTranslations } from 'src/utils/i18nServerProps'
import {
  getOnDemandSocialImage,
  OnDemandCityConfig,
  resolveOnDemandCity
} from 'src/utils/onDemand'
import { toOgLocale } from 'src/utils/seo'

/**
 * Almacenamiento a Domicilio — per-city landing page.
 *
 * Routes: `/renta-mini-bodegas/almacenamiento-a-domicilio/[city]`
 *
 * Server-side guards:
 *  - `pillar` MUST equal `PillarPage.SelfStorage` else 404.
 *  - `city` MUST resolve to a coverage record with `partnerActive: true` else
 *    404. (E.g. `tijuana` is unknown → 404. `guadalajara` is known but partner
 *    inactive → 404.)
 *  - The slug MUST equal the resolved canonical slug. Non-canonical slugs that
 *    map to the same metro area (e.g. `san-pedro-garza-garcia` resolves to
 *    Monterrey) get a 301 to the canonical URL. This keeps a single SEO URL
 *    per market and avoids duplicate-content penalties.
 *
 * Cache headers match the parent: `s-maxage=3600, stale-while-revalidate=86400`.
 */

type IProps = {
  pillar: PillarPage
  locale: string
  city: string
  coverage: OnDemandCityConfig
}

const buildCanonicalUrl = (citySlug: string) =>
  `https://www.spotme.mx/renta-mini-bodegas/almacenamiento-a-domicilio/${citySlug}`

const SPOTME_ORIGIN = 'https://www.spotme.mx'

const Page: NextPage<IProps> = ({ locale, coverage }) => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')

  // Anchor the meta title/description on the city's cheapest duration-tier
  // per-m² rate for SEO + ad copy. Uses `coverage.displayShortName` (e.g.
  // "Monterrey") so the title doesn't blow past Google's ~60-char title
  // limit on cities with long full names like "Ciudad de México y Zona
  // Metropolitana".
  //
  // `numberWithCommas` instead of `.toLocaleString('es-MX')` — Node and
  // browser disagree on the latter's output (NBSP vs comma), which causes
  // React error #418 on SSR'd public pages. See CLAUDE.md "Hydration-Safe
  // Number & Date Formatting".
  const fromPrice = numberWithCommas(coverage.perSqmRates.twelveMonths)
  const title = t('META.TITLE_CITY', {
    city: coverage.displayShortName,
    price: fromPrice
  })
  const description = t('META.DESCRIPTION_CITY', {
    city: coverage.displayShortName,
    price: fromPrice
  })
  const url = buildCanonicalUrl(coverage.canonicalSlug)
  const socialImage = `${SPOTME_ORIGIN}${getOnDemandSocialImage(coverage.canonicalSlug)}`
  const socialImageAlt = t('META.IMAGE_ALT_CITY', {
    city: coverage.displayShortName
  })

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={url}
        openGraph={{
          type: 'website',
          url,
          title,
          description,
          locale: toOgLocale(locale),
          images: [
            {
              url: socialImage,
              width: OG_IMAGE_WIDTH,
              height: OG_IMAGE_HEIGHT,
              alt: socialImageAlt
            }
          ]
        }}
        twitter={{
          cardType: 'summary_large_image',
          site: '@spotme_mx',
          handle: '@spotme_mx'
        }}
      />
      <AlmacenamientoADomicilio variant="city" coverage={coverage} />
    </>
  )
}

export const getServerSideProps: GetServerSideProps<IProps> = async (
  context
) => {
  const { params, locale = 'es', res } = context
  const pillarParam = (params?.pillar as string | undefined) ?? ''
  const cityParam = (params?.city as string | undefined) ?? ''

  if (pillarParam !== PillarPage.SelfStorage) {
    return { notFound: true }
  }

  const resolved = resolveOnDemandCity(cityParam)

  if (!resolved || !resolved.partnerActive) {
    return { notFound: true }
  }

  if (cityParam !== resolved.config.canonicalSlug) {
    return {
      redirect: {
        destination: `/${PillarPage.SelfStorage}/almacenamiento-a-domicilio/${resolved.config.canonicalSlug}`,
        permanent: true
      }
    }
  }

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=3600, stale-while-revalidate=86400'
  )

  const translations = await loadTranslations(locale, [
    'common',
    'nav',
    'footer',
    'page_almacenamiento_a_domicilio',
    // `page_mini_bodega` intentionally NOT loaded — nothing in the
    // AlmacenamientoADomicilio screen tree consumes it. See
    // docs/performance/large-page-data.md.
    // Footer reads `platform_stats`.
    'platform_stats'
  ])

  return {
    props: {
      ...translations,
      pillar: PillarPage.SelfStorage,
      locale,
      city: resolved.config.canonicalSlug,
      coverage: resolved.config
    }
  }
}

export default React.memo(Page)
